import { Controller } from "@hotwired/stimulus"

// Selecting many threads and clearing them in one write.
//
// The selection IS the checkboxes rather than a ledger this controller keeps, and that is the whole
// design. The rows live in an inner Turbo Frame that search, sort, the filter menu and a tab click all
// replace without this element ever being touched, so a remembered selection would outlive the rows it
// described — leaving the bar counting threads that are no longer on the list and a write about to
// reach them. Read from the DOM every time, rows arriving unchecked ARE the reset.
//
// Nothing here submits anything. The ticked ids are stamped onto the bulk forms at submit time, exactly
// as list_context_controller stamps the list's own params, which is why the checkboxes carry no `name`
// and need not sit inside the form they act on — two of those forms could not reach them anyway, one
// being in the sticky header and the other parented to the body.
export default class extends Controller {
  static targets = ["checkbox", "toolbar", "bar", "count"]

  // Both directions, because a re-rendered list arrives as a wave of disconnects followed by a wave of
  // connects, and the bar has to stop describing a selection the moment the rows carrying it leave.
  checkboxTargetConnected() { this.sync() }
  checkboxTargetDisconnected() { this.sync() }

  // The box has already toggled by the time this runs, so a shift-click extends the range to the state
  // it just took. That is what makes shift-clicking a checked box clear the whole run rather than tick
  // it again, which is the behaviour every file list has taught people to expect.
  toggle(event) {
    if (event.shiftKey) this.extendRangeTo(event.target)

    // The thread's id rather than the element, because a row re-rendered under the reader — a live
    // update, a state change on a neighbour — replaces the box while leaving the same thread on the
    // list. Holding the element would silently drop the next shift-click's range.
    this.anchorId = event.target.value
    this.sync()
  }

  // Every row the list rendered. The Spam list's Today segment is what this is for: one tick, this,
  // then Not spam.
  selectAll() {
    this.checkboxTargets.forEach((box) => { box.checked = true })
    this.sync()
  }

  clear() {
    this.checkboxTargets.forEach((box) => { box.checked = false })
    this.sync()
  }

  stamp(event) {
    if (!event.target.matches?.("[data-bulk-action]")) return

    const body = event.detail.formSubmission?.body
    if (!body) return

    for (const box of this.selectedCheckboxes) body.append("conversation_ids[]", box.value)
  }

  // Anchored on the last box touched rather than on the first one still checked, so a selection can be
  // extended in either direction and a second shift-click re-anchors from where the reader just was.
  extendRangeTo(box) {
    const boxes = this.checkboxTargets
    const from = boxes.findIndex((candidate) => candidate.value === this.anchorId)
    const to = boxes.indexOf(box)
    if (from === -1 || to === -1) return

    boxes.slice(Math.min(from, to), Math.max(from, to) + 1)
         .forEach((inRange) => { inRange.checked = box.checked })
  }

  sync() {
    const selected = this.selectedCheckboxes
    const selecting = selected.length > 0

    // On this element rather than on each row, because what it controls is the whole gutter: while a
    // selection exists every row's checkbox stays revealed, and one that vanished between two clicks
    // would turn extending a selection into a hunt for where the last box was.
    this.element.classList.toggle("is-selecting", selecting)
    if (this.hasToolbarTarget) this.toolbarTarget.hidden = selecting
    if (this.hasBarTarget) this.barTarget.hidden = !selecting
    if (this.hasCountTarget) this.countTarget.textContent = `${selected.length} selected`
    if (!selecting) this.anchorId = null
  }

  get selectedCheckboxes() {
    return this.checkboxTargets.filter((box) => box.checked)
  }
}
